import { styled } from '@linaria/react';
import { Group, Progress, Stack, Table } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Initiative } from '../../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import {
  inkLight,
  primaryBase,
  redBase,
  secondaryBase,
  skyDark,
  skyLight,
  skyLightest,
  yellowBase,
} from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import { getRiskCategory } from '../../../adherence/risk-analysis/risk-analysis.helpers';

type InitiativesTableProps = {
  initiatives: Initiative[];
};

type SortField = 'name' | 'progress' | 'totalTasks' | 'totalCompleted' | 'completedThisMonth' | 'riskScore';
type SortDirection = 'asc' | 'desc';

/**
 * Helper to get the latest count from a time-series metric
 *
 * @param {Record<string, number | null>} metric - Time-series metric
 * @returns {number} Latest count
 */
const getLatestCount = (metric: Record<string, number | null> | undefined): number => {
  if (!metric) return 0;
  const dates = Object.keys(metric).sort();
  if (dates.length === 0) return 0;
  const latestDate = dates[dates.length - 1];
  return metric[latestDate] ?? 0;
};

/**
 * Helper to get count for current month from a time-series metric
 *
 * @param {Record<string, number | null>} metric - Time-series metric
 * @returns {number} Count for current month
 */
const getCurrentMonthCount = (metric: Record<string, number | null> | undefined): number => {
  if (!metric) return 0;
  const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM
  return metric[currentMonth] ?? 0;
};

/**
 * Transform Initiative objects into table row data
 *
 * @param {Initiative[]} initiatives - Array of initiatives
 * @returns {any[]} Table row data
 */
const transformInitiativesToTableData = (initiatives: Initiative[]) => {
  return initiatives.map((initiative) => {
    const completedTasks = getLatestCount(initiative.completed_subtasks_over_time?.tasks);
    const totalTasks = getLatestCount(initiative.total_subtasks_over_time?.tasks);
    const completedThisMonth = getCurrentMonthCount(initiative.completed_subtasks_over_time?.tasks);
    const progress = Math.round(initiative.percent_progress || 0);

    return {
      id: initiative.id,
      name: initiative.name,
      key: initiative.key,
      progress,
      portfolio: initiative.portfolio,
      total_tasks: totalTasks,
      total_completed: completedTasks,
      completed_this_month: completedThisMonth,
      risk_score: initiative.flora_annotations?.risk_score,
      executive_summary: initiative.flora_annotations?.executive_summary,
      completed_subtasks_over_time: initiative.completed_subtasks_over_time,
      total_subtasks_over_time: initiative.total_subtasks_over_time,
    };
  });
};

export function InitiativesTable({ initiatives }: InitiativesTableProps) {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const useAI = useGlobalStore((state) => state.useAI);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Apply filters using the same logic as simplified overvie

  // Transform filtered initiatives to table data
  const tableData = useMemo(() => {
    return transformInitiativesToTableData(initiatives);
  }, [initiatives]);

  // Sort the table data
  const sortedInitiatives = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    return [...tableData].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'progress':
          aValue = a.progress;
          bValue = b.progress;
          break;
        case 'totalTasks':
          aValue = a.total_tasks ?? 0;
          bValue = b.total_tasks ?? 0;
          break;
        case 'totalCompleted':
          aValue = a.total_completed ?? 0;
          bValue = b.total_completed ?? 0;
          break;
        case 'completedThisMonth':
          aValue = a.completed_this_month ?? 0;
          bValue = b.completed_this_month ?? 0;
          break;
        case 'riskScore':
          aValue = a.risk_score ?? -1;
          bValue = b.risk_score ?? -1;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tableData, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon name="unfold_more" size={16} color={inkLight} />;
    }
    return sortDirection === 'asc' ? (
      <Icon name="expand_less" size={16} color={secondaryBase} />
    ) : (
      <Icon name="expand_more" size={16} color={secondaryBase} />
    );
  };

  if (!tableData || tableData.length === 0) {
    return (
      <SectionCard>
        <Stack gap={12}>
          <SectionHeader>
            <Icon name="list" size={20} color={skyDark} />
            <SectionTitle>{i18n.t('strategy.overview.sections.initiatives_table')}</SectionTitle>
          </SectionHeader>
          <EmptyState>
            <Icon name="info-circle" size={16} color={skyDark} />
            <Text size="small" color={skyDark}>
              {i18n.t('process_analysis.filters.no_initiatives_available')}
            </Text>
          </EmptyState>
        </Stack>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <Stack gap={12}>
        <TableContainer>
          <Table
            verticalSpacing="md"
            horizontalSpacing="md"
            withRowBorders
            style={{
              width: '100%',
            }}
          >
            <Table.Thead
              style={{
                backgroundColor: skyLightest,
                position: 'sticky',
                top: 0,
                zIndex: 20,
                borderBottom: `1px solid ${skyLight}`,
              }}
            >
              <Table.Tr>
                <Table.Th style={{ width: '25%' }}>
                  <SortableHeader onClick={() => handleSort('name')}>
                    <Text color={inkLight} size="small" weight="medium">
                      {i18n.t('strategy.overview.table.initiative_name').toUpperCase()}
                    </Text>
                    {renderSortIcon('name')}
                  </SortableHeader>
                </Table.Th>
                <Table.Th style={{ width: '15%' }}>
                  <SortableHeader $centered onClick={() => handleSort('progress')}>
                    <Text color={inkLight} size="small" weight="medium">
                      {i18n.t('strategy.overview.table.progress').toUpperCase()}
                    </Text>
                    {renderSortIcon('progress')}
                  </SortableHeader>
                </Table.Th>
                <Table.Th style={{ width: '15%' }}>
                  <SortableHeader $centered onClick={() => handleSort('totalTasks')}>
                    <Text color={inkLight} size="small" weight="medium">
                      {i18n
                        .t('strategy.overview.table.total_tasks', { work_units: i18n.t('common.work_units') })
                        .toUpperCase()}
                    </Text>
                    {renderSortIcon('totalTasks')}
                  </SortableHeader>
                </Table.Th>
                <Table.Th style={{ width: '15%' }}>
                  <SortableHeader $centered onClick={() => handleSort('totalCompleted')}>
                    <Text color={inkLight} size="small" weight="medium">
                      {i18n
                        .t('strategy.overview.table.total_complete', { work_units: i18n.t('common.work_units') })
                        .toUpperCase()}
                    </Text>
                    {renderSortIcon('totalCompleted')}
                  </SortableHeader>
                </Table.Th>
                <Table.Th style={{ width: '15%' }}>
                  <SortableHeader $centered onClick={() => handleSort('completedThisMonth')}>
                    <Text color={inkLight} size="small" weight="medium">
                      {i18n.t('strategy.overview.table.completed_this_month').toUpperCase()}
                    </Text>
                    {renderSortIcon('completedThisMonth')}
                  </SortableHeader>
                </Table.Th>
                {useAI && (
                  <Table.Th style={{ width: '15%' }}>
                    <SortableHeader $centered onClick={() => handleSort('riskScore')}>
                      <Text color={inkLight} size="small" weight="medium">
                        {i18n.t('strategy.overview.table.risk_score').toUpperCase()}
                      </Text>
                      {renderSortIcon('riskScore')}
                    </SortableHeader>
                  </Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedInitiatives.map((initiative) => {
                const riskCategory = getRiskCategory(initiative.risk_score);
                const riskScore = initiative.risk_score ?? null;

                const progressColor =
                  initiative.progress >= 75
                    ? primaryBase
                    : initiative.progress >= 50
                      ? yellowBase
                      : initiative.progress >= 25
                        ? '#FF9800'
                        : redBase;

                return (
                  <Table.Tr
                    key={initiative.id}
                    style={{
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                    }}
                    onClick={() =>
                      initiative.portfolio && navigate(`/application/strategy/${initiative.portfolio}/${initiative.id}`)
                    }
                  >
                    <Table.Td>
                      <Stack gap={2}>
                        <Text weight="medium" size="small" style={{ color: secondaryBase }}>
                          {initiative.name}
                        </Text>
                        {initiative.key && (
                          <Text size="tiny" color={skyDark}>
                            {initiative.key}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Tooltip
                        variant="black"
                        label={
                          <Stack gap={2}>
                            <Text size="medium" weight="medium">
                              Progress
                            </Text>
                            {(() => {
                              const completedTasks = getLatestCount(initiative.completed_subtasks_over_time?.tasks);
                              const totalTasks = getLatestCount(initiative.total_subtasks_over_time?.tasks);
                              const completedPoints = getLatestCount(initiative.completed_subtasks_over_time?.points);
                              const totalPoints = getLatestCount(initiative.total_subtasks_over_time?.points);

                              return (
                                <>
                                  {completedTasks !== null && totalTasks !== null && (
                                    <Text size="small">
                                      Tasks: {completedTasks} / {totalTasks}
                                    </Text>
                                  )}
                                  {completedPoints !== null && totalPoints !== null && (
                                    <Text size="small">
                                      Points: {completedPoints} / {totalPoints}
                                    </Text>
                                  )}
                                </>
                              );
                            })()}
                          </Stack>
                        }
                        zIndex={1000}
                      >
                        <Group gap={4} align="center" wrap="nowrap" style={{ justifyContent: 'center' }}>
                          <Progress
                            value={initiative.progress}
                            color={progressColor}
                            size="sm"
                            radius="xl"
                            style={{ width: '100%' }}
                          />
                          <Text size="tiny" weight="medium">
                            {initiative.progress}%
                          </Text>
                        </Group>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Text size="small" weight="medium">
                        {initiative.total_tasks}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Text size="small" weight="medium">
                        {initiative.total_completed}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Text
                        size="small"
                        weight="medium"
                        color={initiative.completed_this_month > 0 ? primaryBase : skyDark}
                      >
                        {initiative.completed_this_month}
                      </Text>
                    </Table.Td>
                    {useAI && (
                      <Table.Td style={{ textAlign: 'center' }}>
                        {riskScore !== null ? (
                          <Tooltip
                            variant="black"
                            label={
                              <Stack gap={4}>
                                <Text size="medium" weight="medium">
                                  Risk Summary
                                </Text>
                                <Text size="small">{initiative.executive_summary}</Text>
                              </Stack>
                            }
                            maw={450}
                            multiline
                            zIndex={1000}
                          >
                            <RiskBadge $color={riskCategory.color}>
                              <Text size="tiny" weight="bold">
                                {riskScore.toFixed(1)}
                              </Text>
                            </RiskBadge>
                          </Tooltip>
                        ) : (
                          <Text size="tiny" color={skyDark}>
                            N/A
                          </Text>
                        )}
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </TableContainer>
      </Stack>
    </SectionCard>
  );
}

const SectionCard = styled.div`
  border-radius: 16px;
  padding: 20px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`;

const SectionTitle = styled.div`
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  text-align: left;
`;

const TableContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${skyLight};
  background-color: white;
`;

const RiskBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 12px;
  background-color: ${(props) => `${props.$color}20`};
  border: 1px solid ${(props) => props.$color};
  min-width: 40px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: white;
  border-radius: 8px;
  border: 1px dashed ${skyLight};
`;

const SortableHeader = styled.div<{ $centered?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  justify-content: ${(props) => (props.$centered ? 'center' : 'flex-start')};

  &:hover {
    opacity: 0.8;
  }
`;
